import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bookingServiceAPI } from '../services/bookingServiceAPI';
import { bookingService } from '../services/BookingService';
import '../styles/PaymentSuccess.css';

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const bookingId = searchParams.get('booking');
    const paymentId = searchParams.get('id');
    const status = searchParams.get('status');

    if (!bookingId) {
      navigate('/');
      return;
    }

    checkPaymentStatus(bookingId, paymentId, status);
  }, [searchParams, navigate]);

  const checkPaymentStatus = async (bookingId: string, paymentId: string | null, status: string | null) => {
    try {
      setLoading(true);

      // إذا كان الـ status يشير لفشل الدفع، توجه لصفحة الفشل
      if (status && ['failed', 'declined', 'cancelled', 'expired'].includes(status.toLowerCase())) {
        navigate(`/payment-failed?booking=${bookingId}&status=${status}&payment_id=${paymentId || ''}`);
        return;
      }

      // First, get booking data to check actual payment status
      const bookingData = await bookingServiceAPI.getBooking(bookingId);

      // Check if payment is actually completed via webhook
      if (bookingData && bookingData.booking && bookingData.booking.paymentStatus !== 'completed') {
        console.log('⚠️ Payment not completed via webhook, redirecting to payment page');
        navigate(`/payment?booking=${bookingId}`);
        return;
      }

      // Payment was completed via webhook - show success page
      console.log('✅ Payment verified as completed via webhook');

      if (!bookingData || !bookingData.booking) {
        setError('الحجز غير موجود');
        setLoading(false);
        return;
      }

      setBooking(bookingData.booking);
      console.log('✅ Payment completed successfully');
      setLoading(false);
    } catch (err: any) {
      console.error('Error:', err);
      setError('حدث خطأ أثناء التحقق من حالة الدفع');
      setLoading(false);
    }
  };

  const downloadTickets = async () => {
    if (!booking || !booking.tickets || booking.tickets.length === 0) return;
    
    try {
      // هنا يمكن تنزيل التذاكر
      console.log('Downloading tickets...');
    } catch (error) {
      console.error('Error downloading tickets:', error);
    }
  };

  if (loading) {
    return (
      <div className="payment-success-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>جاري التحقق من حالة الدفع...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-container">
        <motion.div 
          className="error-container"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
        >
          <div className="error-icon">❌</div>
          <h1>{error}</h1>
          <button onClick={() => navigate('/')} className="home-button">
            العودة للرئيسية
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      <motion.div 
        className="success-card"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <motion.div 
          className="success-icon"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </motion.div>
        
        <h1>تم الدفع بنجاح!</h1>
        <p>شكراً لك، تمت عملية الدفع بنجاح</p>
        
        {booking && (
          <div className="booking-details">
            <div className="booking-header">
              <div className="booking-code">
                <span className="code-label">رقم الحجز</span>
                <span className="code-value">{booking.bookingCode}</span>
              </div>
              <div className="payment-amount">
                <span className="amount-value">{booking.totalAmount}</span>
                <span className="amount-currency">ريال</span>
              </div>
            </div>
            
            <div className="booking-info-grid">
              <div className="info-card">
                <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
                <div className="info-content">
                  <span className="info-label">العميل</span>
                  <span className="info-value">{booking.customerName}</span>
                </div>
              </div>
              
              <div className="info-card">
                <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
                <div className="info-content">
                  <span className="info-label">تاريخ المناسبة</span>
                  <span className="info-value">{new Date(booking.eventDate).toLocaleDateString('ar-SA', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long'
                  })}</span>
                </div>
              </div>
              
              <div className="info-card">
                <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
                <div className="info-content">
                  <span className="info-label">وقت الفعالية</span>
                  <span className="info-value">{booking.eventTime}</span>
                </div>
              </div>
              
              {booking.eventLocation && (
                <div className="info-card full-width">
                  <div className="info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                  <div className="info-content">
                    <span className="info-label">موقع الفعالية</span>
                    <span className="info-value">{booking.eventLocation}</span>
                  </div>
                </div>
              )}
              
              {booking.boothDisplayName && (
                <div className="info-card full-width">
                  <div className="info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                    <circle cx="12" cy="13" r="4"></circle>
                  </svg>
                </div>
                  <div className="info-content">
                    <span className="info-label">نوع الكابينة</span>
                    <span className="info-value">{booking.boothDisplayName}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="success-actions">
          <button onClick={() => navigate('/')} className="home-button">
            العودة للرئيسية
          </button>
        </div>

        <div className="success-note">
          <div className="note-grid">
            <div className="note-item">
              <div className="note-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4"></path>
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                </svg>
              </div>
              <div className="note-content">
                <span className="note-title">تفاصيل الحجز</span>
                <span className="note-desc">تم إرسال تفاصيل الحجز والتأكيد</span>
              </div>
            </div>
            
            {booking?.bookingType === 'perPhoto' && (
              <div className="note-item">
                <div className="note-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 12v7a2 2 0 002 2h16a2 2 0 002-2v-7"></path>
                    <path d="M2 7h20v5H2z"></path>
                    <path d="M12 3v4"></path>
                    <circle cx="12" cy="17" r="1"></circle>
                  </svg>
                </div>
                <div className="note-content">
                  <span className="note-title">التذاكر</span>
                  <span className="note-desc">تم إرسال جميع التذاكر بصيغة PDF</span>
                </div>
              </div>
            )}
            
            <div className="note-item">
              <div className="note-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                  <path d="M9 16l2 2 4-4"></path>
                </svg>
              </div>
              <div className="note-content">
                <span className="note-title">Google Calendar</span>
                <span className="note-desc">تم إضافة الموعد إلى تقويمك</span>
              </div>
            </div>
          </div>
          
          <div className="email-notification">
            <div className="email-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </div>
            <div className="email-content">
              <span className="email-label">تم الإرسال إلى:</span>
              <span className="email-address">{booking?.email}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default PaymentSuccess;